import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { casesAPI, reportsAPI } from '../services/api';
import toast from 'react-hot-toast';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';
import {
  FolderOpen, Calendar, Send, TrendingUp, AlertTriangle,
  Scale, Clock, MapPin
} from 'lucide-react';
import { format, differenceInDays } from 'date-fns';
import { useAuth } from '../context/AuthContext';

const COLORS = ['#1e3a8a', '#f59e0b', '#10b981', '#ef4444', '#6366f1'];

/* ── Stat Card ───────────────────────────────────────── */
function StatCard({ icon: Icon, label, value, iconColor }) {
  return (
    <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 flex items-center gap-4">
      <div className={`p-3 rounded-xl bg-gray-50 ${iconColor}`}>
        <Icon size={22} />
      </div>
      <div>
        <p className="text-2xl font-bold text-gray-900">{value ?? '—'}</p>
        <p className="text-sm text-gray-500 mt-0.5">{label}</p>
      </div>
    </div>
  );
}

/* ── Upcoming Hearing Item ───────────────────────────── */
function HearingItem({ h, isUrgent }) {
  const dateObj = h.next_hearing_date ? new Date(h.next_hearing_date) : null;
  const daysLeft = dateObj ? differenceInDays(dateObj, new Date()) : null;

  return (
    <Link
      to={`/cases/${h.id}`}
      className={`flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors ${
        isUrgent ? 'bg-red-50' : ''
      }`}
    >
      {/* Date block */}
      <div className="text-center min-w-[40px]">
        {dateObj ? (
          <>
            <p className="text-xs font-bold text-gray-400 uppercase leading-none">
              {format(dateObj, 'MMM')}
            </p>
            <p className="text-xl font-bold text-gray-900 leading-tight">
              {format(dateObj, 'dd')}
            </p>
          </>
        ) : (
          <p className="text-xs text-gray-400">—</p>
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-800 truncate">{h.title}</p>
        <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
          <MapPin size={10} className="shrink-0" />
          <span className="truncate">{h.court_name}</span>
        </p>
      </div>

      {/* Days left */}
      {daysLeft !== null && (
        <div className={`flex items-center gap-1 text-xs font-semibold shrink-0 ${
          daysLeft <= 3 ? 'text-red-500' : 'text-gray-400'
        }`}>
          {daysLeft <= 3
            ? <AlertTriangle size={12} />
            : <Clock size={12} />}
          {daysLeft === 0 ? 'Today' : `${daysLeft}d`}
        </div>
      )}
    </Link>
  );
}

/* ── Dashboard Page ──────────────────────────────────── */
export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [report, setReport] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    casesAPI.dashboardStats().then(r => setStats(r.data)).catch(() => {});
    reportsAPI.dashboard().then(r => setReport(r.data)).catch(() => {});
  }, []);

  const handleSendReport = async () => {
    setSending(true);
    try {
      const res = await reportsAPI.sendDaily();
      toast.success(res.data?.message || 'Daily report sent!');
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to send report';
      toast.error(msg, { duration: 6000 });
    } finally {
      setSending(false);
    }
  };

  const pieData = stats ? [
    { name: 'Active',    value: stats.active_cases    },
    { name: 'Adjourned', value: stats.adjourned_cases },
    { name: 'Disposed',  value: stats.disposed_cases  },
  ] : [];

  const lawyerBarData = report?.lawyer_stats?.map(l => ({
    name: l.name,
    Active:    l.active,
    Adjourned: l.adjourned,
    Disposed:  l.disposed,
  })) || [];

  const upcomingHearings = report?.upcoming_hearings || [];

  return (
    <div className="space-y-6">

      {/* ── Header ─────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-sm text-gray-400 mt-0.5">{format(new Date(), 'EEEE, dd MMMM yyyy')}</p>
        </div>
        {['admin'].includes(user?.role) && (
          <button
            onClick={handleSendReport}
            disabled={sending}
            className="flex items-center gap-2 text-white text-sm font-medium px-4 py-2 rounded-xl transition-colors disabled:opacity-60 hover:opacity-90"
            style={{ backgroundColor: '#1a2744' }}
          >
            <Send size={15} />
            {sending ? 'Sending...' : 'Send Daily Report'}
          </button>
        )}
      </div>

      {/* ── Stat Cards ─────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={FolderOpen}    label="Total Cases"     value={stats?.total_cases}    iconColor="text-blue-600"   />
        <StatCard icon={AlertTriangle} label="Active Cases"    value={stats?.active_cases}   iconColor="text-red-500"    />
        <StatCard icon={Clock}         label="Hearings Today"  value={stats?.hearings_today} iconColor="text-orange-500" />
        <StatCard icon={Scale}         label="This Week"       value={stats?.hearings_this_week} iconColor="text-green-600" />
      </div>

      {/* ── Main Content: Charts left, Upcoming Hearings right ─ */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Charts column (2/3 width) */}
        <div className="lg:col-span-2 space-y-6">

          {/* Pie Chart */}
          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
            <h2 className="font-semibold text-gray-800 mb-4">Case Status Distribution</h2>
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%" cy="45%"
                  outerRadius={85} innerRadius={45}
                  dataKey="value"
                  labelLine={false}
                  label={({ cx, cy, midAngle, innerRadius, outerRadius, value }) => {
                    if (!value) return null;
                    const RADIAN = Math.PI / 180;
                    const r = innerRadius + (outerRadius - innerRadius) * 0.5;
                    const x = cx + r * Math.cos(-midAngle * RADIAN);
                    const y = cy + r * Math.sin(-midAngle * RADIAN);
                    return (
                      <text x={x} y={y} fill="white" textAnchor="middle"
                        dominantBaseline="central" fontSize={13} fontWeight="bold">
                        {value}
                      </text>
                    );
                  }}
                >
                  {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v, n) => [`${v} cases`, n]} />
                <Legend iconType="circle" iconSize={10}
                  formatter={(value, entry) => (
                    <span style={{ color: '#374151', fontSize: 13 }}>
                      {value} <span style={{ color: entry.color, fontWeight: 600 }}>({entry.payload.value})</span>
                    </span>
                  )}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Bar Chart */}
          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
            <h2 className="font-semibold text-gray-800 mb-4">Lawyer-wise Cases</h2>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={lawyerBarData} barSize={16}>
                <XAxis dataKey="name" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip cursor={{ fill: '#f8fafc' }} />
                <Bar dataKey="Active"    fill="#1e3a8a" radius={[4,4,0,0]} />
                <Bar dataKey="Adjourned" fill="#f59e0b" radius={[4,4,0,0]} />
                <Bar dataKey="Disposed"  fill="#10b981" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Upcoming Hearings Panel (1/3 width) */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 flex flex-col overflow-hidden">
          <div className="px-4 py-4 border-b border-gray-100 flex items-center gap-2">
            <Calendar size={16} className="text-blue-600" />
            <h2 className="font-semibold text-gray-800">Upcoming Hearings</h2>
          </div>
          <div className="flex-1 overflow-y-auto divide-y divide-gray-50 p-2">
            {upcomingHearings.length === 0 ? (
              <p className="text-center text-gray-400 text-sm py-10">No upcoming hearings</p>
            ) : upcomingHearings.map((h, i) => {
              const daysLeft = h.next_hearing_date
                ? differenceInDays(new Date(h.next_hearing_date), new Date())
                : null;
              return (
                <HearingItem key={i} h={h} isUrgent={daysLeft !== null && daysLeft <= 3} />
              );
            })}
          </div>
          {upcomingHearings.length > 0 && (
            <div className="px-4 py-3 border-t border-gray-100">
              <Link to="/cases" className="text-xs font-medium text-blue-600 hover:text-blue-800">
                View all cases →
              </Link>
            </div>
          )}
        </div>
      </div>

    </div>
  );
}
